import {
  makeStyles,
  tokens,
  Text,
} from '@coreai/fluentui-react';
import {
  AgentVariantIcon,
  useAgentBadgeClass,
} from '../../helpers/agentVariant';
import { ParameterList } from '../compact/ParameterList';
import { HostedAgentConnectionTab } from '../hostedAgent/HostedAgentConnectionTab';
import { SettingsPanel } from '../settings/SettingsPanel';
import { ConnectionTab } from '../connections/ConnectionTab';
import { ToolConfigTab } from '../tool/ToolConfigTab';
import { McpToolConfigTab } from '../tool/McpToolConfigTab';
import { AgentHarnessTabBody } from './AgentHarnessTabBody';
import { HarnessTabHint } from './HarnessTabHint';
import { KnowledgeBaseSelector } from '../KnowledgeBaseSelector';
import { MockDataTab } from '../mock/MockDataTab';
import { NodeCodeView } from './NodeCodeView';
import { NodeAboutTab } from './NodeAboutTab';
import { NodeAboutCard, NodeAboutDocumentationLink } from './NodeAboutShared';
import type { NodeSettings } from '@/types/nodeSettings';
import type { EditorTab, NodeEditorContextValue } from './useNodeEditorContext';

const useStyles = makeStyles({
  settingsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  settingsGroupTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXS,
  },
  aboutSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: 0,
    maxWidth: '100%',
  },
  aboutDetailRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
  },
  aboutDetailLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    minWidth: '100px',
  },
  aboutDetailValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    minWidth: 0,
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
  },
  aboutDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase300,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
});

interface NodeEditorTabBodyProps {
  tab: EditorTab;
  ctx: NodeEditorContextValue;
  /**
   * Which parameter groups to render for the `'parameters'` tab. Only
   * consulted for `tab === 'parameters'`. Defaults to `'combined'` (the
   * legacy behavior — required + advanced in one list). The drawer passes
   * `'main'` in the Configure section to move Advanced parameters into their
   * own peer section (rendered via `tab === 'advanced-parameters'`).
   */
  parameterListMode?: 'combined' | 'main' | 'advanced-only';
  /**
   * Fired when the {@link HarnessTabHint} link is clicked. The drawer wires
   * it to `goToSection('harness')`; the legacy ExpandedView wires it to
   * `setActiveTab('agent-harness')`. Optional — when absent the hint
   * renders with a disabled link.
   */
  onNavigateToHarness?: () => void;
}

/**
 * NodeEditorTabBody — renders the content for a single node-editor tab.
 *
 * This is the single source of truth for tab bodies, shared by the legacy
 * three-panel `ExpandedView` and the new `NodeEditorDrawer`. The rendering
 * logic is a verbatim extraction of `ExpandedView.renderTabContent`, so both
 * shells produce identical content — only the surrounding chrome differs.
 */
export const NodeEditorTabBody = ({
  tab,
  ctx,
  parameterListMode = 'combined',
  onNavigateToHarness,
}: NodeEditorTabBodyProps) => {
  const styles = useStyles();
  const badgeClass = useAgentBadgeClass();

  const {
    node,
    nodeId,
    nodeType,
    parameterValues,
    isAgent,
    isHostedAgent,
    isFoundryAgent,
    isCodingAgent,
    connectionName,
    toolData,
    liveNodeData,
    liveNodeError,
    settings,
    onSettingsChange,
    settingsValidationErrors,
    isTrigger,
    settingsLoading,
    settingsError,
    availablePredecessors,
    isReadOnly,
  } = ctx;

  if (!node) return null;

  switch (tab) {
    case 'parameters': {
      // Hosted agents keep all authoring in Parameters (no Harness hand-off),
      // so the Harness hint only applies to the coding/foundry variants.
      const showHarnessHint = isCodingAgent || isFoundryAgent;
      if (isHostedAgent) {
        // #2732: hosted agents have no dedicated Connection tab. Agent
        // selection, runtime summary, and parameters all live here,
        // matching the other agent variants (no Harness-tab hand-off).
        return (
          <>
            <HostedAgentConnectionTab />
            <ParameterList
              showAgentParameterButton={!!node?.parentAgentName}
              mode={parameterListMode}
            />
          </>
        );
      }
      return (
        <>
          {showHarnessHint && <HarnessTabHint onNavigate={onNavigateToHarness} />}
          <ParameterList
            showAgentParameterButton={!!node?.parentAgentName}
            mode={parameterListMode}
          />
        </>
      );
    }

    case 'advanced-parameters':
      // Hosted Agent exposes only important parameters in its manifest.
      if (isHostedAgent) return null;
      return (
        <ParameterList
          showAgentParameterButton={!!node?.parentAgentName}
          mode="advanced-only"
        />
      );

    case 'connection':
      // Hosted agents relocate their selection UI to the Parameters tab (#2732),
      // so this tab is only reachable for non-hosted connection nodes.
      return (
        <ConnectionTab
          nodeId={node.id}
          nodeType={nodeType}
          connectionName={connectionName}
        />
      );

    case 'knowledge':
      return <KnowledgeBaseSelector />;

    case 'settings':
      return (
        <SettingsPanel
          settings={settings ?? ({} as NodeSettings)}
          onChange={onSettingsChange}
          isTrigger={isTrigger}
          availablePredecessors={availablePredecessors}
          isLoading={settingsLoading}
          error={settingsError}
          validationErrors={settingsValidationErrors}
          readOnly={isReadOnly}
        />
      );

    case 'agent-harness':
      return <AgentHarnessTabBody ctx={ctx} />;

    case 'tool':
      return (
        <ToolConfigTab
          key={node.id}
          toolName={node.toolName ?? node.name}
          {...(toolData?.description && { toolDescription: toolData.description })}
          {...(toolData?.agentParameterSchema && {
            agentParameterSchema: toolData.agentParameterSchema as Record<
              string,
              unknown
            >,
          })}
          {...(parameterValues['_workflowId']
            ? { targetWorkflowId: parameterValues['_workflowId'] as string }
            : {})}
        />
      );

    case 'mcp-config':
      if (!toolData || !node.parentAgentName) return null;
      return (
        <McpToolConfigTab
          agentName={node.parentAgentName}
          toolName={node.toolName ?? node.name}
          toolData={toolData}
        />
      );

    case 'code':
      return (
        <>
          {liveNodeError && <Text role="alert">{liveNodeError} Showing the saved definition, not your pending edits.</Text>}
          <NodeCodeView
            value={liveNodeData ?? node.nodeData}
            ariaLabel={`${node.name} JSON`}
          />
        </>
      );

    case 'mock':
      return nodeId ? <MockDataTab nodeId={nodeId} /> : null;

    case 'about': {
      if (!isAgent) return <NodeAboutTab node={node} parameterValues={parameterValues} />;
      const agentVariant = isCodingAgent ? 'managed' : isFoundryAgent ? 'foundry' : isHostedAgent ? 'hosted' : 'native';
      return (
        <div className={styles.aboutSection} data-testid="node-about-agent">
          {/* Node Header */}
          <NodeAboutCard name={node.name} subtitle="AI Agent"
            icon={<AgentVariantIcon variant={agentVariant} />} iconClassName={badgeClass(agentVariant)} />

          {/* Node Details */}
          <div className={styles.settingsGroup}>
            <Text className={styles.settingsGroupTitle}>Details</Text>
            <div className={styles.aboutDetailRow}>
              <Text className={styles.aboutDetailLabel}>Type:</Text>
              <Text className={styles.aboutDetailValue}>{nodeType}</Text>
            </div>
            <div className={styles.aboutDetailRow}>
              <Text className={styles.aboutDetailLabel}>Category:</Text>
              <Text className={styles.aboutDetailValue}>AI Agent</Text>
            </div>
            {node.parentAgentName && (
              <div className={styles.aboutDetailRow}>
                <Text className={styles.aboutDetailLabel}>Parent Agent:</Text>
                <Text className={styles.aboutDetailValue}>
                  {node.parentAgentName}
                </Text>
              </div>
            )}
          </div>

          {/* Description */}
          <div className={styles.settingsGroup}>
            <Text className={styles.settingsGroupTitle}>Description</Text>
            <Text className={styles.aboutDescription}>
              An AI-powered agent that can process natural language instructions and execute complex tasks using available tools.
            </Text>
          </div>

          {/* Documentation Link */}
          <div className={styles.settingsGroup}>
            <Text className={styles.settingsGroupTitle}>Resources</Text>
            <NodeAboutDocumentationLink />
          </div>
        </div>
      );
    }

    default: {
      // Compile-time exhaustiveness guard: if a new EditorTab literal is
      // added to the union but not routed above, TypeScript errors here so
      // the missing case can't ship silently.
      const _exhaustive: never = tab;
      void _exhaustive;
      return null;
    }
  }
};

NodeEditorTabBody.displayName = 'NodeEditorTabBody';
